"use client";

import { memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ThreadListPrimitive } from "@assistant-ui/react";
import {
  ArchiveIcon,
  ArrowDownAZIcon,
  ClockIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PinIcon,
  PinOffIcon,
  PlusIcon,
  SearchIcon,
} from "lucide-react";
import {
  type ThreadItem,
} from "@/components/custom/contexts/threadContext";
import { useRouteContext } from "@/components/custom/contexts/routeContext";
import { useThreadActions } from "@/components/custom/providers/customThreadListProvider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useThreadsListQuery } from "./useThreadsListQuery";
import { appEvents } from "@/lib/events";

// ─── Filter / sort vocabulary ──────────────────────────────────────────────

type SortMode = "recent" | "az";

const PAGE_SIZE = 25;

// ─── Page ──────────────────────────────────────────────────────────────────

export function ChatsListPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inputRef = useRef<HTMLInputElement>(null);
  const locale = i18n.language || "cs";
  const routeContext = useRouteContext();

  const initialQuery = searchParams.get("q") ?? "";
  const initialSort = (searchParams.get("sort") ?? "recent") as SortMode;
  const initialContent = searchParams.get("content") === "1";

  const [query, setQuery] = useState(initialQuery);
  const [sort, setSort] = useState<SortMode>(
    initialSort === "az" ? "az" : "recent",
  );
  const [searchInContent, setSearchInContent] = useState(initialContent);

  // Defer the search query so React batches keystrokes — the network fetch
  // hits the deferred value rather than firing once per character.
  const deferredQuery = useDeferredValue(query);

  // Reflect state in URL — replace, not push, so back/forward isn't polluted
  // by every keystroke.
  useEffect(() => {
    const params = new URLSearchParams();
    if (deferredQuery.trim()) params.set("q", deferredQuery.trim());
    if (sort !== "recent") params.set("sort", sort);
    if (searchInContent) params.set("content", "1");
    const search = params.toString();
    navigate(search ? `/chats?${search}` : "/chats", { replace: true });
  }, [deferredQuery, sort, searchInContent, navigate]);

  // Cmd/Ctrl+K refocuses the search box from anywhere on the page.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const {
    items: threadItems,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    refetch,
  } = useThreadsListQuery({
    q: deferredQuery,
    searchInContent,
    pageSize: PAGE_SIZE,
    caseId: routeContext.launchCaseId ?? routeContext.caseId,
  });

  const total = threadItems.length;

  // Refresh the list when a thread's UpdatedAt has shifted on the server, so a
  // /chats tab kept open in parallel to another tab where the user is chatting
  // sees the active thread surface at the top. Backend bump is synchronous on
  // user-message-sent; turn-completed catches any classification / generated-
  // document bumps from the assistant turn. Stale-while-revalidate in the hook
  // keeps the list rendered through the refetch — no list-flash.
  useEffect(() => {
    const offSent = appEvents.on("user-message-sent", () => refetch());
    const offDone = appEvents.on("turn-completed", () => refetch());
    return () => {
      offSent();
      offDone();
    };
  }, [refetch]);

  // Inline rename state — keyed by remoteId so only one row ever edits at once.
  const [renamingId, setRenamingId] = useState<string | null>(null);

  // Stable callbacks so React.memo on ThreadRow can short-circuit unchanged rows.
  const handleRenameStart = useCallback((id: string) => setRenamingId(id), []);
  const handleRenameEnd = useCallback(() => setRenamingId(null), []);

  const pinnedItems = useMemo(
    () => threadItems.filter(item => item.isPinned),
    [threadItems],
  );
  const unpinnedItems = useMemo(
    () => threadItems.filter(item => !item.isPinned),
    [threadItems],
  );

  // Server returns rows ordered by IsPinned > UpdatedAt > CreatedAt > Id;
  // for sort=az we re-order client-side over the loaded prefix only. Note
  // this means Show more in az mode can surface earlier-alphabetic items
  // mid-list — known tradeoff documented in CHANGELOG.
  const sortedUnpinned = useMemo(() => {
    if (sort === "recent") return unpinnedItems;
    return [...unpinnedItems].sort((a, b) => titleCompare(a, b, locale));
  }, [unpinnedItems, sort, locale]);

  const sortedPinned = useMemo(() => {
    if (sort === "recent") return pinnedItems;
    return [...pinnedItems].sort((a, b) => titleCompare(a, b, locale));
  }, [pinnedItems, sort, locale]);

  const groups = useMemo(
    () => buildGroups({
      pinnedItems: sortedPinned,
      unpinnedItems: sortedUnpinned,
      sort,
      query: deferredQuery,
      t,
    }),
    [sortedPinned, sortedUnpinned, sort, deferredQuery, t],
  );

  const showEmpty =
    !isLoading &&
    threadItems.length === 0 &&
    deferredQuery.trim().length > 0;

  return (
    <div className="praetor-chats-page flex h-full flex-col items-center overflow-y-auto px-6 py-16">
      <div className="w-full max-w-[760px]">
        <header className="mb-6 flex items-end justify-between">
          <div>
            <h1 className="font-ui text-[22px] font-semibold tracking-[-0.01em] text-foreground">
              {t("chatsPage.title")}
            </h1>
            <p className="mt-1 text-[13px] text-muted-foreground">
              {t("chatsPage.subtitle", { count: total })}
            </p>
          </div>
          <ThreadListPrimitive.New asChild>
            <Link
              to={`/${routeContext.launchCaseId ?? "default"}`}
              className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-foreground px-3.5 py-2 text-[13px] font-medium text-background transition-opacity hover:opacity-90"
            >
              <PlusIcon className="size-3.5" strokeWidth={1.7} />
              {t("chatsPage.newChat")}
            </Link>
          </ThreadListPrimitive.New>
        </header>

        <div className="praetor-chats-search-wrapper relative mb-2">
          <SearchIcon
            className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            strokeWidth={1.7}
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t("chatsPage.searchPlaceholder")}
            aria-label={t("chatsPage.searchPlaceholder")}
            autoFocus
            className="praetor-chats-search h-11 w-full rounded-[10px] border border-border bg-background pl-10 pr-11 text-[14px] outline-none transition-shadow placeholder:text-muted-foreground focus:border-ring focus:shadow-[0_0_0_3px_oklch(0.55_0.03_264/0.10)]"
          />
          <kbd className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded-[5px] border border-border/70 bg-muted/50 px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
            ⌘K
          </kbd>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 py-3.5">
          <label className="inline-flex cursor-pointer select-none items-center gap-1.5 text-[12.5px] text-muted-foreground transition-colors hover:text-foreground">
            <input
              type="checkbox"
              checked={searchInContent}
              onChange={e => setSearchInContent(e.target.checked)}
              className="size-3.5 cursor-pointer accent-foreground"
            />
            {t("chatsPage.searchInContent")}
          </label>

          <button
            type="button"
            onClick={() => setSort(s => (s === "recent" ? "az" : "recent"))}
            className="ml-auto inline-flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1.5 text-[12.5px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            {sort === "recent" ? (
              <ClockIcon className="size-3.5" strokeWidth={1.7} />
            ) : (
              <ArrowDownAZIcon className="size-3.5" strokeWidth={1.7} />
            )}
            {sort === "recent" ? t("chatsPage.sortRecent") : t("chatsPage.sortAz")}
          </button>
        </div>

        {showEmpty ? (
          <div className="py-16 text-center text-[13.5px] text-muted-foreground">
            <span className="mb-1.5 block text-[15px] font-medium text-foreground/70">
              {t("chatsPage.emptyTitle")}
            </span>
            {t("chatsPage.emptyHint")}
          </div>
        ) : (
          <>
            <ul role="list" className="praetor-chats-list" aria-busy={isLoading}>
              {groups.map(group => (
                <li key={group.key} className="mb-4">
                  <h2 className="mb-1 flex items-center gap-2 border-b border-border/50 px-1 pb-2 font-ui text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
                    <span>{group.label}</span>
                    <span className="font-normal text-muted-foreground/60">
                      · {group.items.length}
                    </span>
                  </h2>
                  <ul role="list">
                    {group.items.map(item => (
                      <ThreadRow
                        key={item.remoteId}
                        item={item}
                        query={deferredQuery}
                        locale={locale}
                        isRenaming={renamingId === item.remoteId}
                        onRenameStart={handleRenameStart}
                        onRenameEnd={handleRenameEnd}
                        onAfterMutation={refetch}
                      />
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
            {hasMore && (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  disabled={isLoadingMore}
                  onClick={loadMore}
                  className="praetor-chats-show-more inline-flex cursor-pointer items-center gap-2 rounded-md border border-border/70 bg-background px-3.5 py-2 text-[13px] text-muted-foreground transition-colors hover:border-border hover:bg-muted hover:text-foreground disabled:cursor-default disabled:opacity-60"
                >
                  <span>
                    {isLoadingMore
                      ? t("common.loading")
                      : t("chatsPage.showMore", { count: PAGE_SIZE })}
                  </span>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Grouping ──────────────────────────────────────────────────────────────

type Group = { key: string; label: string; items: ThreadItem[] };

function buildGroups({
  pinnedItems,
  unpinnedItems,
  sort,
  query,
  t,
}: {
  pinnedItems: ThreadItem[];
  unpinnedItems: ThreadItem[];
  sort: SortMode;
  query: string;
  t: (key: string) => string;
}): Group[] {
  const result: Group[] = [];

  if (pinnedItems.length > 0) {
    result.push({
      key: "pinned",
      label: t("chatsPage.sectionPinned"),
      items: pinnedItems,
    });
  }

  if (unpinnedItems.length === 0) return result;

  if (sort === "az") {
    result.push({
      key: "alpha",
      label: t("chatsPage.sectionAlpha"),
      items: unpinnedItems,
    });
    return result;
  }

  // sort === "recent"
  if (query.trim().length > 0) {
    result.push({
      key: "results",
      label: t("chatsPage.sectionResults"),
      items: unpinnedItems,
    });
    return result;
  }

  // Time buckets — Today / Yesterday / This week / Older. Items arrive
  // already sorted newest-first so each bucket preserves recency order.
  const now = new Date();
  const today = startOfDay(now);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const todayItems: ThreadItem[] = [];
  const yesterdayItems: ThreadItem[] = [];
  const thisWeekItems: ThreadItem[] = [];
  const olderItems: ThreadItem[] = [];

  for (const item of unpinnedItems) {
    const ts = item.updatedAt ?? item.createdAt;
    if (!ts) {
      olderItems.push(item);
      continue;
    }
    const d = new Date(ts);
    if (d >= today) todayItems.push(item);
    else if (d >= yesterday) yesterdayItems.push(item);
    else if (d >= weekAgo) thisWeekItems.push(item);
    else olderItems.push(item);
  }

  if (todayItems.length) result.push({ key: "today", label: t("chatsPage.sectionToday"), items: todayItems });
  if (yesterdayItems.length) result.push({ key: "yesterday", label: t("chatsPage.sectionYesterday"), items: yesterdayItems });
  if (thisWeekItems.length) result.push({ key: "thisWeek", label: t("chatsPage.sectionThisWeek"), items: thisWeekItems });
  if (olderItems.length) result.push({ key: "older", label: t("chatsPage.sectionOlder"), items: olderItems });

  return result;
}

function titleCompare(a: ThreadItem, b: ThreadItem, locale: string): number {
  return (a.title ?? "").localeCompare(b.title ?? "", locale);
}

// ─── Row ───────────────────────────────────────────────────────────────────

type ThreadRowProps = {
  item: ThreadItem;
  query: string;
  locale: string;
  isRenaming: boolean;
  /** Receives the row's remoteId so the parent owns a single stable callback. */
  onRenameStart: (remoteId: string) => void;
  onRenameEnd: () => void;
  onAfterMutation: () => void;
};

const ThreadRow = memo(function ThreadRow({
  item,
  query,
  locale,
  isRenaming,
  onRenameStart,
  onRenameEnd,
  onAfterMutation,
}: ThreadRowProps) {
  const { t } = useTranslation();
  const caseIdSegment = item.caseId ?? "default";
  const href = `/${caseIdSegment}/${item.remoteId}`;
  const startRename = useCallback(
    () => onRenameStart(item.remoteId),
    [onRenameStart, item.remoteId],
  );

  const timestamp = formatRelativeTimestamp(
    item.updatedAt ?? item.createdAt,
    locale,
  );
  const titleText = item.title ?? t("threadList.newConversation");

  // Row layout has two zones: a navigable Link covering icon/title/meta/timestamp,
  // and a non-navigable menu sibling on the right. Keeping the menu OUT of the Link
  // tree eliminates any chance of a menu-item click bubbling into the underlying
  // navigation, regardless of Radix's portal/event semantics.
  const rowClass = cn(
    "praetor-chats-row group flex items-center gap-3.5 border-b border-border/50 px-1 py-3.5 transition-colors last:border-b-0",
    !isRenaming && "hover:bg-muted/40",
  );
  const linkClass = cn(
    "flex min-w-0 flex-1 items-center gap-3.5",
    !isRenaming && "cursor-pointer",
  );

  const titleSpan = (
    <span className="mb-0.5 truncate text-[14.5px] font-medium text-foreground">
      {!isRenaming && item.isPinned && (
        <PinIcon
          className="mr-1 inline-block size-3 align-[-2px] text-muted-foreground"
          strokeWidth={1.7}
        />
      )}
      {isRenaming ? (
        <RenameInput
          initial={titleText}
          ariaLabel={t("chatsPage.renameLabel")}
          onCommit={onAfterMutation}
          remoteId={item.remoteId}
          onClose={onRenameEnd}
        />
      ) : (
        <HighlightedTitle title={titleText} query={query} locale={locale} />
      )}
    </span>
  );

  const linkInner = (
    <>
      <div className="flex min-w-0 flex-1 flex-col">
        {titleSpan}
      </div>

      {timestamp && !isRenaming && (
        <span className="shrink-0 tabular-nums text-[12px] text-muted-foreground">
          {timestamp}
        </span>
      )}
    </>
  );

  return (
    <li role="listitem" className={rowClass}>
      {isRenaming ? (
        <div className={linkClass}>{linkInner}</div>
      ) : (
        <Link to={href} className={linkClass}>
          {linkInner}
        </Link>
      )}
      {!isRenaming && (
        <ThreadRowMenu
          item={item}
          onRenameStart={startRename}
          onAfterMutation={onAfterMutation}
        />
      )}
    </li>
  );
});

function RenameInput({
  initial,
  ariaLabel,
  remoteId,
  onCommit,
  onClose,
}: {
  initial: string;
  ariaLabel: string;
  remoteId: string;
  onCommit: () => void;
  onClose: () => void;
}) {
  const { rename } = useThreadActions();
  const [value, setValue] = useState(initial);

  const commit = async () => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === initial.trim()) {
      onClose();
      return;
    }
    await rename(remoteId, trimmed);
    onClose();
    onCommit();
  };

  return (
    <input
      type="text"
      value={value}
      autoFocus
      aria-label={ariaLabel}
      onFocus={e => e.currentTarget.select()}
      onChange={e => setValue(e.target.value)}
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onKeyDown={e => {
        if (e.key === "Enter") {
          e.preventDefault();
          void commit();
        } else if (e.key === "Escape") {
          e.preventDefault();
          onClose();
        }
      }}
      onBlur={onClose}
      className="-mx-1 w-full min-w-0 rounded-sm border-0 bg-transparent px-1 text-[14.5px] font-medium text-foreground outline-none focus:ring-1 focus:ring-ring/40"
    />
  );
}

function ThreadRowMenu({
  item,
  onRenameStart,
  onAfterMutation,
}: {
  item: ThreadItem;
  onRenameStart: () => void;
  onAfterMutation: () => void;
}) {
  const { t } = useTranslation();
  const { pin, unpin, archive } = useThreadActions();
  const isPinned = item.isPinned ?? false;

  // Belt-and-suspenders: the menu sits as a sibling of the row's Link, so
  // bubbling shouldn't reach it anyway, but preventDefault on each select
  // makes the "no navigation" guarantee explicit and survives any future
  // refactor that re-nests the menu inside a navigable wrapper.
  const handlePinToggle = (e: Event) => {
    e.preventDefault();
    if (!item.remoteId) return;
    if (isPinned) void unpin(item.remoteId);
    else void pin(item.remoteId);
  };

  const handleRename = (e: Event) => {
    e.preventDefault();
    onRenameStart();
  };

  const handleArchive = async (e: Event) => {
    e.preventDefault();
    if (!item.remoteId) return;
    await archive(item.remoteId);
    onAfterMutation();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t("threadList.moreOptions")}
          className="flex size-[26px] shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-border hover:text-foreground group-hover:opacity-100 data-[state=open]:opacity-100"
        >
          <MoreHorizontalIcon className="size-4" strokeWidth={1.7} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="end"
        className="min-w-[180px]"
      >
        <DropdownMenuItem onSelect={handlePinToggle}>
          {isPinned ? (
            <PinOffIcon className="size-4" strokeWidth={1.7} />
          ) : (
            <PinIcon className="size-4" strokeWidth={1.7} />
          )}
          {isPinned ? t("threadList.unpin") : t("threadList.pin")}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={handleRename}>
          <PencilIcon className="size-4" strokeWidth={1.7} />
          {t("chatsPage.menuRename")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={handleArchive}>
          <ArchiveIcon className="size-4" strokeWidth={1.7} />
          {t("threadList.archive")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Highlight + timestamp helpers ─────────────────────────────────────────

function HighlightedTitle({
  title,
  query,
  locale,
}: {
  title: string;
  query: string;
  locale: string;
}) {
  const trimmed = query.trim();
  if (!trimmed) return <>{title}</>;
  const lowerTitle = title.toLocaleLowerCase(locale);
  const lowerQuery = trimmed.toLocaleLowerCase(locale);
  const idx = lowerTitle.indexOf(lowerQuery);
  if (idx === -1) return <>{title}</>;
  return (
    <>
      {title.slice(0, idx)}
      <mark className="rounded-sm bg-[oklch(0.92_0.08_90)] px-0.5 text-foreground dark:bg-[oklch(0.45_0.10_90)] dark:text-white">
        {title.slice(idx, idx + trimmed.length)}
      </mark>
      {title.slice(idx + trimmed.length)}
    </>
  );
}

function formatRelativeTimestamp(
  iso: string | undefined,
  locale: string,
): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const today = startOfDay(now);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const timeFmt = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (date >= today) {
    return `${localizedToday(locale)}, ${timeFmt.format(date)}`;
  }
  if (date >= yesterday) {
    return `${localizedYesterday(locale)}, ${timeFmt.format(date)}`;
  }

  if (date.getFullYear() === now.getFullYear()) {
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
    }).format(date);
  }
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

function localizedToday(locale: string): string {
  switch (locale.split("-")[0]) {
    case "cs":
    case "sk":
      return "Dnes";
    case "hu":
      return "Ma";
    default:
      return "Today";
  }
}

function localizedYesterday(locale: string): string {
  switch (locale.split("-")[0]) {
    case "cs":
    case "sk":
      return "Včera";
    case "hu":
      return "Tegnap";
    default:
      return "Yesterday";
  }
}
